// Falar em vez de digitar — mesmo padrão do donomais.
// Computador: ditado do navegador (texto aparece enquanto fala). Celular: grava e
// transcreve no servidor (funciona no iPhone). Igual ao áudio do WhatsApp: segurar
// grava, soltar termina, arrastar ◀ cancela, arrastar ▲ trava; toque rápido grava
// travado (com Cancelar / Pronto). O texto vai para a caixa e quem envia é a pessoa.
import { useEffect, useRef, useState } from 'react'

const NativeSR = () => (typeof window !== 'undefined' ? window.SpeechRecognition || window.webkitSpeechRecognition : null)
const isMobile = () => typeof navigator !== 'undefined' && (/android|iphone|ipad|ipod/i.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1))
const canRecord = () => typeof window !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && !!window.MediaRecorder
let nativeBroken = false // se o ditado do navegador falhar uma vez, passa a gravar
const useNative = () => !!NativeSR() && !isMobile() && !nativeBroken
const MAX_SECONDS = 180
const pickMime = () => ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/aac'].find((m) => window.MediaRecorder.isTypeSupported?.(m)) || ''

// onText(texto, final): chamado ao vivo (ditado) e no fim.
// transcribe(blob) → Promise<string>: manda o áudio gravado para o servidor.
export function useDictation(onText, transcribe) {
  const [state, setState] = useState('idle') // idle | listening | working
  const [seconds, setSeconds] = useState(0)
  const [error, setError] = useState('')
  const [level, setLevel] = useState(0)
  const cancelled = useRef(false)
  const raf = useRef(null)
  const audioCtx = useRef(null)
  const rec = useRef(null)
  const media = useRef(null)
  const timer = useRef(null)
  const cb = useRef(onText)
  cb.current = onText
  const tr = useRef(transcribe)
  tr.current = transcribe

  useEffect(() => () => cleanup(), []) // eslint-disable-line react-hooks/exhaustive-deps

  function cleanup() {
    clearInterval(timer.current)
    cancelAnimationFrame(raf.current)
    setLevel(0)
    media.current?.stream?.getTracks().forEach((t) => t.stop())
    audioCtx.current?.close?.().catch(() => {})
    audioCtx.current = null
  }

  // Mede o volume do microfone (mostra que está escutando)
  function meter(stream) {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext
      const ctx = new Ctx()
      audioCtx.current = ctx
      const an = ctx.createAnalyser()
      an.fftSize = 512
      ctx.createMediaStreamSource(stream).connect(an)
      const data = new Uint8Array(an.fftSize)
      const tick = () => {
        an.getByteTimeDomainData(data)
        let sum = 0
        for (const v of data) sum += ((v - 128) / 128) ** 2
        setLevel(Math.min(1, Math.sqrt(sum / data.length) * 4))
        raf.current = requestAnimationFrame(tick)
      }
      tick()
    } catch {
      /* sem medidor: segue gravando */
    }
  }

  function startNative() {
    const r = new (NativeSR())()
    r.lang = 'pt-BR'
    r.interimResults = true
    r.maxAlternatives = 1
    let finalText = ''
    r.onresult = (e) => {
      let live = ''
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalText += e.results[i][0].transcript
        else live += e.results[i][0].transcript
      }
      cb.current((finalText + live).trim(), false)
    }
    r.onend = () => {
      clearInterval(timer.current)
      if (finalText.trim()) cb.current(finalText.trim(), true)
      setState('idle')
    }
    r.onerror = (e) => {
      clearInterval(timer.current)
      if (['not-allowed', 'service-not-allowed', 'network', 'audio-capture'].includes(e.error)) {
        nativeBroken = !!canRecord()
        setError(e.error === 'not-allowed' ? 'Libere o microfone para o site e tente de novo.' : 'Toque no microfone de novo para gravar.')
      }
      setState('idle')
    }
    rec.current = r
    r.start()
  }

  async function startRecording() {
    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      setError('Libere o microfone para o site e tente de novo.')
      setState('idle')
      return
    }
    const mime = pickMime()
    const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined)
    const chunks = []
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data)
    mr.onstop = async () => {
      cleanup()
      if (cancelled.current) return setState('idle')
      const blob = new Blob(chunks, { type: mr.mimeType || mime || 'audio/webm' })
      if (blob.size < 800) {
        setError('Não ouvi nada. Toque e fale de novo.')
        return setState('idle')
      }
      setState('working')
      try {
        const text = await tr.current(blob)
        if (text) cb.current(text, true)
        else setError('Não entendi. Tente de novo.')
      } catch (e) {
        setError(e?.message || 'Não entendi. Tente de novo ou digite.')
      } finally {
        setState('idle')
      }
    }
    media.current = mr
    meter(stream)
    navigator.vibrate?.(30)
    mr.start()
  }

  const start = () => {
    cancelled.current = false
    setError('')
    setSeconds(0)
    setState('listening')
    clearInterval(timer.current)
    timer.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) stop()
        return s + 1
      })
    }, 1000)
    if (useNative()) startNative()
    else startRecording()
  }
  const stop = () => {
    clearInterval(timer.current)
    if (rec.current) {
      try { rec.current.stop() } catch { /* já parado */ }
      rec.current = null
    }
    if (media.current && media.current.state === 'recording') media.current.stop()
  }
  const cancel = () => {
    cancelled.current = true
    if (rec.current) {
      try { rec.current.abort?.() } catch { /* já parado */ }
      rec.current = null
    }
    stop()
    setState('idle')
  }

  return { state, listening: state === 'listening', working: state === 'working', seconds, level, error, start, stop, cancel, supported: !!NativeSR() || canRecord() }
}

// Tudo que o campo de mensagem precisa: ditado + gesto de segurar + aviso "confira e envie".
// onText(texto final) recebe a fala; o componente cuida do resto.
export function useVoiceComposer({ onText, transcribe, disabled }) {
  const [live, setLive] = useState('')
  const [fromVoice, setFromVoice] = useState(false)
  const voice = useDictation((t, final) => {
    if (!final) return setLive(t)
    setLive('')
    if (!t.trim()) return
    onText(t.trim())
    setFromVoice(true)
  }, transcribe)
  const [hold, setHoldState] = useState(null) // null | 'holding' | 'locked'
  const holdMode = useRef(null)
  const setHold = (v) => { holdMode.current = v; setHoldState(v) }
  const [drag, setDrag] = useState({ x: 0, y: 0 })
  const stopRef = useRef(null)
  stopRef.current = voice.stop
  useEffect(() => {
    if (!voice.listening) { setHold(null); setDrag({ x: 0, y: 0 }) }
  }, [voice.listening])

  const pressMic = (e) => {
    if (disabled || voice.working || !voice.supported) return
    e.preventDefault()
    setLive('')
    voice.start()
    setHold('holding')
    const o = { x: e.clientX, y: e.clientY, t: Date.now() }
    const move = (ev) => {
      const dx = Math.min(0, ev.clientX - o.x)
      const dy = Math.min(0, ev.clientY - o.y)
      setDrag({ x: dx, y: dy })
      if (dx < -90) { voice.cancel(); end() }
      else if (dy < -80) { setHold('locked'); end() }
    }
    const up = () => {
      end()
      setDrag({ x: 0, y: 0 })
      if (holdMode.current !== 'holding') return
      if (Date.now() - o.t < 350) return setHold('locked') // toque rápido: fica gravando
      setHold(null)
      stopRef.current() // soltou: termina e escreve na caixa
    }
    function end() {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
  }

  return { voice, live, hold, drag, pressMic, fromVoice, clearHint: () => setFromVoice(false), active: voice.listening, disabled }
}

const Svg = ({ d, className, fill }) => (
  <svg className={className} viewBox="0 0 24 24" fill={fill ? 'currentColor' : 'none'} stroke={fill ? 'none' : 'currentColor'} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">{d}</svg>
)
const MicIcon = ({ className }) => <Svg className={className} d={<><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M19 10v1a7 7 0 0 1-14 0v-1" /><path d="M12 18v4" /></>} />
const XIcon = ({ className }) => <Svg className={className} d={<path d="M18 6 6 18M6 6l12 12" />} />
const CheckIcon = ({ className }) => <Svg className={className} d={<path d="M20 6 9 17l-5-5" />} />
const SpinIcon = ({ className }) => <Svg className={`${className} animate-spin`} d={<path d="M21 12a9 9 0 1 1-6.2-8.6" />} />

function Timer({ seconds }) {
  return <span className="w-9 shrink-0 text-sm font-bold tabular-nums text-red-600">{Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}</span>
}

function VoiceLevel({ level = 0, bars = 18 }) {
  return (
    <span className="flex h-6 items-center gap-[3px]" aria-hidden>
      {Array.from({ length: bars }, (_, i) => {
        const wave = 0.35 + 0.65 * Math.abs(Math.sin((i + 1) * 1.7))
        const h = Math.max(0.15, Math.min(1, level * 1.6 * wave))
        return <span key={i} className="w-[3px] rounded-full bg-red-500 transition-[height] duration-75" style={{ height: `${h * 100}%` }} />
      })}
    </span>
  )
}

// Barra que fica no lugar do campo enquanto grava.
export function VoiceRecording({ v }) {
  const { voice, live, hold, drag } = v
  if (hold === 'holding') {
    return (
      <div className="relative flex min-h-[48px] select-none items-center gap-2 rounded-xl bg-red-50 px-3 ring-2 ring-red-200 dark:bg-red-950/30 dark:ring-red-900">
        <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-red-500" />
        <Timer seconds={voice.seconds} />
        <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-500" style={{ transform: `translateX(${drag.x}px)`, opacity: 1 + drag.x / 120 }}>
          ‹ arraste para cancelar
        </span>
        <span className="absolute -top-16 right-1 flex w-11 flex-col items-center gap-0.5 rounded-full bg-white py-2 text-gray-500 shadow-md ring-1 ring-gray-100 dark:bg-gray-800 dark:ring-gray-700" style={{ transform: `translateY(${Math.max(drag.y, -60) / 3}px)` }}>
          <Svg className="h-4 w-4" d={<><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>} />
          <Svg className="h-4 w-4 animate-bounce" d={<path d="m18 15-6-6-6 6" />} />
        </span>
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-500 text-white shadow-lg" style={{ transform: `translate(${drag.x}px, ${drag.y}px) scale(${1 + voice.level * 0.25})` }}>
          <MicIcon className="h-6 w-6" />
        </span>
      </div>
    )
  }
  return (
    <div className="flex min-h-[48px] items-center gap-2 rounded-xl bg-red-50 px-2 ring-2 ring-red-200 dark:bg-red-950/30 dark:ring-red-900">
      <button type="button" onClick={voice.cancel} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-gray-500 hover:bg-white dark:hover:bg-gray-800" aria-label="Cancelar gravação">
        <XIcon className="h-5 w-5" />
      </button>
      <span className="h-2.5 w-2.5 shrink-0 animate-pulse rounded-full bg-red-500" />
      <Timer seconds={voice.seconds} />
      <span className="min-w-0 flex-1 truncate text-sm text-gray-600 dark:text-gray-300">{live || <VoiceLevel level={voice.level} />}</span>
      <button type="button" onClick={voice.stop} className="flex h-10 shrink-0 items-center gap-1 rounded-lg bg-red-500 px-3 text-sm font-bold text-white" aria-label="Terminei de falar">
        <CheckIcon className="h-4 w-4" /> Pronto
      </button>
    </div>
  )
}

// Botão do microfone (aparece no lugar do enviar quando a caixa está vazia).
export function MicButton({ v, className = '' }) {
  const { voice, pressMic, disabled } = v
  return (
    <button
      type="button"
      disabled={disabled || voice.working || !voice.supported}
      onPointerDown={pressMic}
      onContextMenu={(e) => e.preventDefault()}
      className={`flex h-10 w-10 shrink-0 touch-none select-none items-center justify-center rounded-full bg-green-500 text-white shadow-sm transition hover:bg-green-600 disabled:opacity-50 ${className}`}
      aria-label="Falar"
      title="Segure para gravar e solte quando terminar (ou toque uma vez)"
    >
      {voice.working ? <SpinIcon className="h-5 w-5" /> : <MicIcon className="h-5 w-5" />}
    </button>
  )
}

// Aviso acima do campo: "escrevi o que você falou" ou o erro do microfone.
export function VoiceHint({ v, text }) {
  const { voice, fromVoice } = v
  if (voice.listening) return null
  if (voice.working) return <p className="mb-2 text-center text-sm text-gray-500 dark:text-gray-400">Escrevendo o que você falou…</p>
  if (voice.error) return <p className="mb-2 text-center text-sm font-semibold text-red-500">{voice.error}</p>
  if (fromVoice && text?.trim()) return <p className="mb-2 rounded-xl bg-green-50 px-3 py-2 text-center text-sm font-bold text-green-700 dark:bg-green-950/30 dark:text-green-300">✓ Escrevi o que você falou. Confira e toque em enviar ➤</p>
  return null
}

// Manda o áudio para o servidor e devolve o texto.
export async function transcribeAudio(url, headers, blob) {
  const form = new FormData()
  form.append('audio', blob, 'voz')
  const r = await fetch(url, { method: 'POST', headers, body: form })
  const d = await r.json().catch(() => ({}))
  if (!r.ok) throw new Error(d.error || 'Não entendi. Tente de novo ou digite.')
  return d.text || ''
}
